import React, { useState } from 'react';
import { Tooltip, Spin, Input } from 'antd';
import { CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import { formatUserLocalDate3 } from '@utils/widget.js';
import alert from '@components/alert';
import { message } from 'antd';
import ProjectRoleTooltip from '@components/projectRoleTooltip/ProjectRoleTooltip';
import { API } from '@service';
import {
  findDomainCheckItem,
  toFlatDomainCheckResult,
} from './utils/clientDomainCheck';
import ResentEmailTitleCell from './components/resentEmailTitleCell/index.jsx';
import ResentEmailBodyCell from './components/resentEmailBodyCell/index.jsx';
// import NameTooltip from '@components/nameTooltip/NameTooltip.js';

const formatDateTime = (date) => {
  if (!date) {
    return '-';
  }
  return formatUserLocalDate3(date);
};

const resentEmail = async (data = [], projectName = '', projectId = '') => {
  const result = await API.resendinvite({
    clientUsers: data,
    projectName,
    projectId,
  });
  if (
    Array.isArray(result) &&
    result.every((item) => item.result !== 'Failed')
  ) {
    message.success('Resend successful');
  } else {
    message.error('Resend failure');
  }
};

// Email validation API function
export const validateEmailAPI = async (email) => {
  try {
    const response = await API.validateClientEmails({ emails: [email] });
    const item = findDomainCheckItem(response, email);
    return toFlatDomainCheckResult(item);
  } catch (error) {
    if (error?.response?.data) {
      const item = findDomainCheckItem(error.response.data, email);
      if (item) {
        return toFlatDomainCheckResult(item);
      }
      return error.response.data;
    }
    if (error?.isPresent !== undefined) {
      return error;
    }
    console.error('Email validation error:', error);
    return toFlatDomainCheckResult(null);
  }
};

export const tableColumns = ({
  editInfo,
  setEditInfo,
  data,
  handleRemove,
  remove,
  matchGroups,
  copyAllEmails,
  isSettingPage,
  isEditing = () => false,
  handleInputBlur = () => {},
  onEmailValidation = () => {},
  onEmailChange = () => {},
  validatingEmailIndex = null,
  form, // Add form prop
  projectName = '',
  projectId = '',
}) => [
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    editable: isEditing && data && data[data.length - 1]?.name ? false : true,
    comProps: {
      onEmailChange: (rowData, index) => onEmailChange(rowData, index),
      // Add suffix for loading indicator
      suffix: (index) => {
        if (validatingEmailIndex === index) {
          return <LoadingOutlined style={{ color: '#1890ff' }} />;
        }
        return null;
      },
    },
    rules: [
      {
        required: true,
        type: 'email',
        message: 'Correct email is required!',
      },
      ({ getFieldsValue }) => ({
        validator(_, value) {
          const clientEmails = (getFieldsValue()?.clientList || []).map(
            (e) => e.email,
          );
          const hasDuplicateEmail = clientEmails.some(
            (e, i, arr) => arr.indexOf(e) !== i,
          );
          if (hasDuplicateEmail) {
            return Promise.reject(new Error('Email has already been added.'));
          }
          return Promise.resolve();
        },
      }),
    ],
    width: isSettingPage ? '12%' : '20%',
    // Custom render for editing mode to show loading
    renderFormItem: (_, { record }, form) => {
      const index = record?.index;
      const isValidating = validatingEmailIndex === index;

      return (
        <Input
          suffix={
            isValidating ? (
              <LoadingOutlined style={{ color: '#1890ff' }} />
            ) : null
          }
          disabled={isValidating}
        />
      );
    },
  },
  {
    title: <>Name&ensp;</>,
    dataIndex: 'name',
    key: 'name',
    editable: false,
    width: isSettingPage ? '12%' : '12%',
  },
  {
    title: (
      <>
        Project Role&ensp;
        {ProjectRoleTooltip}
      </>
    ),
    dataIndex: 'role_name',
    key: 'role_name',
    editable: true,
    comProps: {
      maxLength: 50,
      onBlur: (data, index) => handleInputBlur(data),
    },
    width: isSettingPage ? '12%' : '16%',
  },
  {
    title: 'Status',
    dataIndex: 'inviteStatus',
    key: 'inviteStatus',
    width: isSettingPage ? '10%' : '12%',
    render: (_, record, index) => {
      const value = record.inviteStatus;

      if (!value) {
        return null;
      }

      const statusTooltipFlag =
        record.inviteStatus === 'failed' || record.inviteStatus === 'Failed';

      return (
        <div className="status-badge-container">
          <p className={`status-badge ${value.toLowerCase()}`}>
            {value}
            {statusTooltipFlag ? (
              <Tooltip
                title={
                  <div className="eu-based-tooltip">{record.comments}</div>
                }
                placement="right"
              >
                <span className="appkiticon icon-information-fill info-icon p-4 info-icon-size" />
              </Tooltip>
            ) : null}
          </p>
        </div>
      );
    },
  },
  {
    title: () => (
      <div>
        EU Based&ensp;
        <Tooltip
          title={
            <div className="eu-based-tooltip">
              To comply with GDPR Requirements, please check the box for EU
              Based users. Visit our{' '}
              <a href="/gateway/privacy" target="_blank">
                Privacy Policy
              </a>{' '}
              for more information.
            </div>
          }
        >
          <span className="appkiticon icon-information-fill" />
        </Tooltip>
      </div>
    ),
    dataIndex: 'isEUBased',
    key: 'isEUBased',
    width: '8%',
    align: 'center',
    editable: true,
    inputType: 'checkbox',
    render: (_, record, index) =>
      data?.[index]?.isEUBased ? <CheckOutlined /> : '',
  },
  {
    title: 'Last Visit',
    dataIndex: 'lastVisit',
    key: 'lastVisit',
    width: '13%',
    render: (_, record) => formatDateTime(record.lastVisit),
    sorter: (a, b) =>
      new Date(b.lastVisit).getTime() - new Date(a.lastVisit).getTime(),
  },
  {
    title: (
      <span onClick={copyAllEmails} className="pointer-cursor">
        Copy All Emails
      </span>
    ),
    dataIndex: 'name',
    key: 'name',
    width: '8%',
    render: (email, record) => (
      <span
        style={{ position: 'relative', top: 4 }}
        className="appkiticon icon-duplicate-outline a-font-24"
        onClick={() => {
          message.destroy();
          navigator.clipboard.writeText(record.email);
          message.success('Email address copied!');
        }}
      />
    ),
  },
  ...(isSettingPage
    ? [
        {
          title: (props) => (
            <ResentEmailTitleCell
              {...props}
              tableData={data}
              resentEmail={(userData) =>
                resentEmail(userData, projectName, projectId)
              }
            />
          ),
          dataIndex: 'resent',
          key: 'resent',
          width: '13%',
          render: (_, record = {}) => {
            if (isEditing(record)) {
              return null;
            }

            return (
              <ResentEmailBodyCell
                record={record}
                resentEmail={(userData) =>
                  resentEmail(userData, projectName, projectId)
                }
              />
            );
          },
        },
      ]
    : []),
];
